import { renderCards, CardDescriptor, CardBadge } from './cardRenderer';

/**
 * 核验扫码 / 装车扫码 模块
 *
 * 数据结构 TripBoxItem（行程箱明细）：
 *  - tripBoxItemNo: 行程箱明细唯一号
 *  - pickingOrderNo: 拣货单号
 *  - tripOrderNo: 行程单号
 *  - boxModel: 箱型（卡片标题）
 *  - barcode: 箱条码（扫码标识 + 生成条形码）
 *  - length/width/height/sizeUnit: 尺寸
 *  - weight/weightUnit: 重量
 *  - isCheck: 是否已核验（核验扫码操作此字段）
 *  - isLoaded: 是否已装车（装车扫码操作此字段）
 *
 * 核验、装车共用同一查询接口（同一份箱明细），仅扫码操作的状态字段不同。
 * 查询接口：GET /api/wms/v1/trip/boxItems/test?bizTripOrderNo=xxx
 * 扫码确认接口：待提供（暂前端翻转状态 mock）。
 */
export interface TripBoxItem {
    tripBoxItemNo: string;
    pickingOrderNo: string;
    tripOrderNo: string;
    boxModel: string;
    barcode: string;
    verifyMark?: string;  // 核验后的核验码（装车阶段已核验时用于生成条码）
    needChangeLabel?: boolean;  // 是否需换标（换物流标签），仅核验阶段使用
    logisticsBarcode?: string;  // 物流码（换标件展示，与平台码 barcode 同屏；装车阶段未核验件展示取此值）
    length: number;
    width: number;
    height: number;
    weight: number;
    isCheck: boolean;
    isLoaded: boolean;
    sizeUnit: string;
    weightUnit: string;
}

interface ApiResponse<T> {
    success: boolean;
    code: number;
    message: string;
    data: T;
}

type ScanMode = 'check' | 'load';

const API_BASE_URL = '/api/wms/v1';
const COOKIE_STORAGE_KEY = 'sorting_plan_cookie';

const CONTAINER_ID: Record<ScanMode, string> = {
    check: 'checkDetailsContainer',
    load: 'loadDetailsContainer'
};
const QUERY_INPUT_ID: Record<ScanMode, string> = {
    check: 'checkQueryNo',
    load: 'loadQueryNo'
};
const QUERY_BTN_ID: Record<ScanMode, string> = {
    check: 'queryCheckPlan',
    load: 'queryLoadPlan'
};
const MODE_LABEL: Record<ScanMode, string> = {
    check: '核验',
    load: '装车'
};

const itemsByMode: Record<ScanMode, TripBoxItem[]> = {
    check: [],
    load: []
};

export function initTripScanning() {
    (['check', 'load'] as ScanMode[]).forEach(mode => {
        bindQueryEvents(mode);
    });
}

function bindQueryEvents(mode: ScanMode) {
    const queryBtn = document.getElementById(QUERY_BTN_ID[mode]);
    const queryInput = document.getElementById(QUERY_INPUT_ID[mode]) as HTMLInputElement | null;

    queryBtn?.addEventListener('click', () => queryTripBoxItems(mode));
    queryInput?.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') queryTripBoxItems(mode);
    });
}

// 只提取必要的认证字段 SID 和 TK（与分拣扫描一致）
function extractAuthCookies(fullCookie: string): string {
    const cookies = fullCookie.split(';').map(c => c.trim());
    const sid = cookies.find(c => c.startsWith('SID='));
    const tk = cookies.find(c => c.startsWith('TK='));
    return [sid, tk].filter(Boolean).join('; ');
}

async function queryTripBoxItems(mode: ScanMode) {
    const queryInput = document.getElementById(QUERY_INPUT_ID[mode]) as HTMLInputElement | null;
    const bizTripOrderNo = queryInput?.value.trim();

    if (!bizTripOrderNo) {
        alert('请输入业务行程单号');
        return;
    }

    const cookie = localStorage.getItem(COOKIE_STORAGE_KEY);
    if (!cookie) {
        alert('请先在顶部设置 Cookie');
        return;
    }

    const authCookie = extractAuthCookies(cookie);
    showLoading(mode, '正在查询箱明细...');

    const url = `${API_BASE_URL}/trip/boxItems/test?bizTripOrderNo=${encodeURIComponent(bizTripOrderNo)}`;

    console.log(`[${mode}] 查询业务行程单号:`, bizTripOrderNo, '请求URL:', url);

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: { 'X-Cookie': authCookie }
        });

        const responseText = await response.text();
        console.log(`[${mode}] 响应状态:`, response.status, '响应内容:', responseText);

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${responseText}`);
        }

        const result: ApiResponse<TripBoxItem[]> = JSON.parse(responseText);

        if (result.success && result.data) {
            itemsByMode[mode] = result.data;
            renderTripBoxItems(mode, itemsByMode[mode]);
        } else {
            showEmpty(mode, result.message || '未找到箱明细');
        }
    } catch (error) {
        console.error(`[${mode}] 查询箱明细失败:`, error);
        showError(mode, '查询箱明细失败，请检查网络连接和 Cookie 设置');
    }
}

function renderTripBoxItems(mode: ScanMode, items: TripBoxItem[]) {
    const primaryField: 'isCheck' | 'isLoaded' = mode === 'check' ? 'isCheck' : 'isLoaded';
    const doneCount = items.filter(it => it[primaryField]).length;

    const sorted = [...items].sort((a, b) => {
        if (a[primaryField] !== b[primaryField]) return a[primaryField] ? 1 : -1;
        return a.boxModel.localeCompare(b.boxModel);
    });

    // 唛头取值预计算（含未核验去重序号）；核验/装车两套模式共用
    // 传入 sorted：序号按卡片展示顺序分配，使 /1、/2、/3 与页面卡片顺序一致
    const marks = buildCardMarks(sorted);
    // 物流码重编（重复/撞号时按展示顺序加 /1、/2 序号）：
    //  - 装车：未核验件物流码作唛头展示
    //  - 核验：换标件物流码作双码左列展示
    const logisticsMarks = renumberLogistics(mode, sorted, marks);

    const cards: CardDescriptor[] = sorted.map(it => {
        // 装车未核验件：物流码（含序号）优先，缺省回退唛头；核验模式唛头始终走 marks
        const { title, titleSuffix, barcodeValue } = mode === 'load'
            ? logisticsMarks.get(it.tripBoxItemNo) ?? describeCardMark(it, marks)
            : describeCardMark(it, marks);
        return {
            id: it.tripBoxItemNo,
            title,
            titleSuffix,
            info: `${it.weight} KGS<br>${calcVolumeCbm(it)} CBM`,
            barcodeValue,
            qrcodeValue: barcodeValue,
            // 换标双码仅核验阶段；装车（load）模式不附带，行为不变
            dualCode: mode === 'check' ? buildDualCode(it, marks, logisticsMarks) : undefined,
            badges: buildBadges(mode, it)
        };
    });

    renderCards(
        CONTAINER_ID[mode],
        cards,
        `共 <span class="details-count">${items.length}</span> 件，已${MODE_LABEL[mode]} <span class="details-count">${doneCount}</span> 件`,
        () => queryTripBoxItems(mode),
        '暂无箱明细数据'
    );
}

// 状态徽章：
// 核验 tab：仅核验状态（未核验红 / 已核验绿）
// 装车 tab：未核验只显示核验状态；已核验只显示装车状态（未装车黄 / 已装车绿）
function buildBadges(mode: ScanMode, item: TripBoxItem): CardBadge[] {
    const checkBadge: CardBadge = item.isCheck
        ? { text: '已核验', className: 'status-green' }
        : { text: '未核验', className: 'status-red' };
    if (mode === 'check') return [checkBadge];
    if (!item.isCheck) return [checkBadge];
    const loadBadge: CardBadge = item.isLoaded
        ? { text: '已装车', className: 'status-green' }
        : { text: '未装车', className: 'status-yellow' };
    return [loadBadge];
}

// 唛头取值预计算（核验/装车两套模式共用，按 tripBoxItemNo 索引）：
//  - 已核验(isCheck)：展示核验唛头 verifyMark（缺省回退 barcode）——两套模式行为一致
//  - 未核验：唛头=barcode；若同 barcode 重复，全部加序号 /1、/2、/3，
//    且重新编码后的 {barcode}/{序号} 不得与已核验项的 verifyMark 冲突，冲突则递增序号直至无冲突。
//    特别地：即便未核验项唯一（无重复），若其裸 barcode 与已核验 verifyMark 撞号，也需加序号避让。
// 注：序号去重仅作用于未核验项；已核验项始终展示 verifyMark，不受影响。
// 返回每条明细的：展示标题 title、可选后缀 titleSuffix(单独配色)、条码取值 barcodeValue(条码/二维码/复制均用此值)。
export type CheckMark = { title: string; titleSuffix?: string; barcodeValue: string };

export function buildCardMarks(items: TripBoxItem[]): Map<string, CheckMark> {
    const result = new Map<string, CheckMark>();
    // 已核验项的 verifyMark 作为"已占用"基准，用于冲突检测
    const usedMarks = new Set<string>();
    for (const it of items) {
        if (!it.isCheck) continue;
        const mark = it.verifyMark || it.barcode;
        result.set(it.tripBoxItemNo, { title: mark, barcodeValue: mark });
        if (it.verifyMark) usedMarks.add(it.verifyMark);
    }

    // 统计未核验项中 barcode 的重复条数
    const dupCount = new Map<string, number>();
    for (const it of items) {
        if (it.isCheck) continue;
        dupCount.set(it.barcode, (dupCount.get(it.barcode) ?? 0) + 1);
    }

    // 保留传入顺序（即卡片展示顺序）分配序号，使序号与页面卡片顺序一致
    const unverified = items.filter(it => !it.isCheck);

    for (const it of unverified) {
        const isDup = (dupCount.get(it.barcode) ?? 0) > 1;
        // 裸唛头未被占用且非重复：直接用裸唛头
        if (!isDup && !usedMarks.has(it.barcode)) {
            result.set(it.tripBoxItemNo, { title: it.barcode, barcodeValue: it.barcode });
            continue;
        }
        // 重复，或裸唛头与已核验 verifyMark 撞号：取最小且不冲突的序号，并立即占用
        let n = 1;
        while (usedMarks.has(`${it.barcode}/${n}`)) n++;
        const full = `${it.barcode}/${n}`;
        usedMarks.add(full);
        result.set(it.tripBoxItemNo, { title: it.barcode, titleSuffix: `/${n}`, barcodeValue: full });
    }
    return result;
}

// 换标件（needChangeLabel）双码信息：仅核验模式使用。
//  - needChangeLabel !== true 或 logisticsBarcode 为空 → 不附带（undefined），回退单码
//  - 左列物流码 = 重编后的完整值（重复/撞号时含 /1、/2 序号，见 renumberLogistics），缺失回退原始物流码
//  - 右列平台码 = 唛头值（已核验 verifyMark || barcode，与卡片唛头体系一致，不改 verifyMark 逻辑）
export function buildDualCode(
    item: TripBoxItem,
    marks: Map<string, CheckMark>,
    logisticsMarks: Map<string, CheckMark>
): { logistics: string; platform: string } | undefined {
    if (item.needChangeLabel !== true || !item.logisticsBarcode) return undefined;
    const platform = marks.get(item.tripBoxItemNo)?.barcodeValue ?? item.barcode;
    const logistics = logisticsMarks.get(item.tripBoxItemNo)?.barcodeValue ?? item.logisticsBarcode;
    return { logistics, platform };
}

// 单张卡片唛头描述：从预计算结果取值，缺失时回退到原始 barcode
function describeCardMark(item: TripBoxItem, marks: Map<string, CheckMark>): CheckMark {
    return marks.get(item.tripBoxItemNo) ?? { title: item.barcode, barcodeValue: item.barcode };
}

// 物流码重编预计算（按 tripBoxItemNo 索引，仅含参与项；核验/装车共用同一套规则）：
//  - 参与项：装车=未核验且物流码非空（作唛头展示）；核验=换标件(needChangeLabel)且物流码非空（作双码左列）
//  - 多条共用同一物流码：按展示顺序全部加序号 /1、/2、/3，文本值/条码/二维码/复制均用重编后的完整值
//  - 物流码唯一且不与页面上已有唛头值（verifyMark / 唛头序号码）冲突：保持原值（同样计入占用）
//  - 序号避让：不占用已有码值，冲突则递增直至无冲突（与 buildCardMarks 同一套约束）
export function renumberLogistics(mode: ScanMode, items: TripBoxItem[], marks: Map<string, CheckMark>): Map<string, CheckMark> {
    const include: (it: TripBoxItem) => boolean = mode === 'load'
        ? it => !it.isCheck && !!it.logisticsBarcode
        : it => it.needChangeLabel === true && !!it.logisticsBarcode;

    const result = new Map<string, CheckMark>();
    // 已占用 = 全部唛头值，保证页面各卡片扫码值全局唯一
    const used = new Set<string>();
    for (const m of marks.values()) used.add(m.barcodeValue);

    // 参与项（类型收窄：物流码非空）
    const participants = items.filter(
        (it): it is TripBoxItem & { logisticsBarcode: string } => include(it) && !!it.logisticsBarcode
    );

    // 统计参与项中同一物流码的重复条数
    const dupCount = new Map<string, number>();
    for (const it of participants) {
        dupCount.set(it.logisticsBarcode, (dupCount.get(it.logisticsBarcode) ?? 0) + 1);
    }

    // 保留传入顺序（即卡片展示顺序）分配序号
    for (const it of participants) {
        const code = it.logisticsBarcode;
        // 唯一且裸值未被占用：直接用原值；同时计入占用，避免与后续序号码撞号
        if ((dupCount.get(code) ?? 0) === 1 && !used.has(code)) {
            used.add(code);
            result.set(it.tripBoxItemNo, { title: code, barcodeValue: code });
            continue;
        }
        // 重复，或裸值与已有唛头撞号：取最小且不冲突的序号，并立即占用
        let n = 1;
        while (used.has(`${code}/${n}`)) n++;
        const full = `${code}/${n}`;
        used.add(full);
        result.set(it.tripBoxItemNo, { title: code, titleSuffix: `/${n}`, barcodeValue: full });
    }
    return result;
}

// 体积计算：长×宽×高，cm 换算为 CBM（立方米），保留 3 位小数
function calcVolumeCbm(item: TripBoxItem): string {
    const cubic = item.length * item.width * item.height;
    const cbm = item.sizeUnit && item.sizeUnit.toLowerCase() === 'm' ? cubic : cubic / 1_000_000;
    return cbm.toFixed(3);
}

function showLoading(mode: ScanMode, message: string) {
    const container = document.getElementById(CONTAINER_ID[mode]);
    if (!container) return;
    container.innerHTML = `
        <div class="loading-state">
            <div class="loading-spinner"></div>
            <p>${message}</p>
        </div>
    `;
}

function showEmpty(mode: ScanMode, message: string) {
    const container = document.getElementById(CONTAINER_ID[mode]);
    if (!container) return;
    container.innerHTML = `
        <div class="empty-state">
            <svg width="48" height="48" viewBox="0 0 20 20" fill="none" opacity="0.3">
                <rect x="2" y="2" width="16" height="16" rx="2" stroke="currentColor" stroke-width="2"/>
                <path d="M2 7H18" stroke="currentColor" stroke-width="2"/>
                <path d="M7 2V7" stroke="currentColor" stroke-width="2"/>
                <path d="M13 2V7" stroke="currentColor" stroke-width="2"/>
            </svg>
            <p>${message}</p>
        </div>
    `;
}

function showError(mode: ScanMode, message: string) {
    const container = document.getElementById(CONTAINER_ID[mode]);
    if (!container) return;
    container.innerHTML = `
        <div class="error-state">
            <svg width="48" height="48" viewBox="0 0 20 20" fill="none" opacity="0.3">
                <circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="2"/>
                <path d="M10 6V10M10 14H10.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <p class="error-message">${message}</p>
        </div>
    `;
}
